import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Send, ShieldCheck, Trash2, FileText, Upload } from "lucide-react";

const STATUSES = ["todo", "in_progress", "done"];

const STATUS_BTN = {
  todo: "bg-amber-500 text-white border-amber-500",
  in_progress: "bg-blue-600 text-white border-blue-600",
  done: "bg-green-600 text-white border-green-600",
};

export default function TaskDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [docs, setDocs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const [t, c, u, d] = await Promise.all([
      api.get(`/tasks/${id}`),
      api.get(`/tasks/${id}/comments`),
      api.get("/users").catch(() => ({ data: [] })),
      api.get(`/documents?task_id=${id}`),
    ]);
    setTask(t.data);
    setComments(c.data);
    setUsers(u.data);
    setDocs(d.data);
  };
  useEffect(() => { load(); }, [id]);

  const setStatus = async (status) => {
    await api.patch(`/tasks/${id}`, { status });
    toast.success("Status updated");
    load();
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/tasks/${id}/comments`, { text });
      setText("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSending(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    navigate("/tasks");
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("task_id", id);
    try {
      await api.post("/documents/upload", fd);
      toast.success("Attached");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeDoc = async (docId) => {
    await api.delete(`/documents/${docId}`);
    toast.success("Removed");
    load();
  };

  if (!task) return <div className="p-10 label-uppercase">Loading…</div>;
  const userMap = Object.fromEntries(users.map((u) => [u.id, u.name]));
  const canEdit = task.can_edit !== false;

  return (
    <div className="min-h-screen" data-testid="task-detail-page">
      <header className="border-b border-zinc-200 page-px py-6">
        <Link to="/tasks" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to tasks
        </Link>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="label-uppercase mb-1">Task</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter break-words" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{task.title}</h1>
          </div>
          <div className="flex gap-1 flex-wrap">
            {canEdit ? (
              <>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    data-testid={`set-status-${s}`}
                    className={`text-[10px] uppercase tracking-wider px-3 py-1.5 border ${
                      task.status === s ? STATUS_BTN[s] : "border-zinc-300 hover:bg-zinc-50"
                    }`}
                  >{s.replace("_"," ")}</button>
                ))}
                {user.role === "admin" && (
                  <button onClick={remove} data-testid="delete-task-button" className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1">
                    <Trash2 size={11} /> Delete
                  </button>
                )}
              </>
            ) : (
              <span className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-800 flex items-center gap-1.5">
                Read only · {task.status.replace("_"," ")}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        <aside className="lg:col-span-4 lg:border-r border-b lg:border-b-0 border-zinc-200 p-6 sm:p-8 space-y-5">
          <Section label="Details">
            <Row k="Status" v={<span className="capitalize">{task.status.replace("_"," ")}</span>} />
            <Row k="Priority" v={<span className="capitalize">{task.priority}</span>} />
            <Row k="Assignee" v={userMap[task.assignee_id] || "Unassigned"} />
            <Row k="Created by" v={userMap[task.created_by] || "—"} />
            <Row k="Due" v={task.due_date || "—"} />
            <Row k="Created" v={new Date(task.created_at).toLocaleDateString()} />
          </Section>
          {task.description && (
            <Section label="Description">
              <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-wrap">{task.description}</p>
            </Section>
          )}

          <Section label={`Attachments · ${docs.length}`}>
            {canEdit ? (
              <label className="block border border-dashed border-zinc-300 p-4 text-center cursor-pointer hover:bg-zinc-50 hover:border-[#002FA7] transition-colors" data-testid="task-attach-input">
                <input type="file" onChange={upload} className="hidden" disabled={uploading} />
                <Upload size={16} className="mx-auto mb-1 text-zinc-500" />
                <div className="text-xs text-zinc-600">{uploading ? "Uploading…" : "Click to attach a file"}</div>
                <div className="text-[10px] text-zinc-400 mt-1 uppercase tracking-wider">Max 25 MB</div>
              </label>
            ) : (
              <div className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-200 px-3 py-2 bg-zinc-50">
                Read-only · attachments cannot be added
              </div>
            )}
            {docs.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid="task-attachments-list">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border border-zinc-200 px-2.5 py-2 text-xs hover:bg-zinc-50">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 truncate hover:text-[#002FA7] flex-1 min-w-0"
                      data-testid={`task-doc-${d.id}`}
                    >
                      <FileText size={12} className="text-zinc-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{d.filename}</div>
                        <div className="text-[10px] text-zinc-400 uppercase tracking-wider mt-0.5">
                          {d.uploaded_by_name} · {(d.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    </a>
                    <button
                      onClick={() => removeDoc(d.id)}
                      disabled={!canEdit}
                      className="text-zinc-400 hover:text-red-600 ml-2 disabled:opacity-30 disabled:hover:text-zinc-400"
                      data-testid={`task-doc-delete-${d.id}`}
                      title={canEdit ? "Remove" : "Read-only"}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </aside>

        <section className="lg:col-span-8 flex flex-col" data-testid="task-chat-panel">
          <div className="border-b border-zinc-200 px-4 sm:px-8 py-4 flex items-center gap-2 bg-zinc-50">
            <ShieldCheck size={16} className="text-[#002FA7]" />
            <span className="font-medium tracking-tight">Discussion</span>
            <span className="ml-auto label-uppercase">{comments.length} messages</span>
          </div>
          <div className="flex-1 p-4 sm:p-8 space-y-4 max-h-[60vh] overflow-y-auto">
            {comments.length === 0 && (
              <div className="text-sm text-zinc-500 text-center py-12">No messages yet. Start the discussion below.</div>
            )}
            {comments.map((m) => {
              const mine = m.author_id === user.id;
              return (
                <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`comment-${m.id}`}>
                  <div className={`w-7 h-7 flex items-center justify-center text-[10px] font-bold uppercase shrink-0 ${
                    mine ? "bg-ikb text-white" : "bg-zinc-900 text-white"
                  }`}>{m.author_name?.[0] || "?"}</div>
                  <div className={`max-w-[75%] ${mine ? "text-right" : ""}`}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      {m.author_name} {m.author_role === "admin" && "· admin"} · {new Date(m.created_at).toLocaleString()}
                    </div>
                    <div className={`inline-block text-sm px-3 py-2 border ${
                      mine ? "bg-[#EAF0FF] border-[#B6C8F1]" : "bg-white border-zinc-200"
                    }`}>{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <form onSubmit={send} className="border-t border-zinc-200 p-4 flex gap-2">
            <input
              data-testid="task-comment-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={canEdit ? "Write a message…" : "Read only — cannot reply on team tasks"}
              disabled={!canEdit}
              className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7] disabled:bg-zinc-50 disabled:text-zinc-400"
            />
            <button
              type="submit"
              disabled={sending || !canEdit}
              data-testid="task-comment-send"
              className="bg-ikb text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#00227A] disabled:opacity-50"
            >
              <Send size={14} /> Send
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <div className="label-uppercase mb-2">{label}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between text-sm border-b border-zinc-100 py-1.5">
      <span className="text-zinc-500">{k}</span>
      <span className="text-zinc-900">{v}</span>
    </div>
  );
}
